// Ready for rider: the hub-wide handoff board (docs/PHASE7-CONTRACT.md "Partner Hub"). One card per
// order with every station's progress, so the packer hands the rider the whole order. Pure: no DOM,
// no Intl, so node --test can check it.

/** @typedef {import('@popcart/core/v1').HandoffOrder} HandoffOrder */
/** @typedef {HandoffOrder['stations'][number]} HandoffStation */
/** @typedef {import('@popcart/core/v1').ProductLine} ProductLine */

/** The board refetches every 10 s (and the layout's 5 s queue poll nudges it on new orders). */
export const HANDOFF_MS = 10_000;

/** Station names on screen: the data key `mart` reads "Grocery" (B3.10). */
export const STATION_NAME = /** @type {const} */ ({ fresh: 'Fresh', grill: 'Grill', mart: 'Grocery' });

/** Station row state: a word and a tone, so the state never rests on colour alone. */
export const STATE_VIEW = /** @type {const} */ ({
  done: { label: 'Done', tone: 'success' },
  working: { label: 'Working', tone: 'warning' },
  waiting: { label: 'Waiting', tone: 'neutral' },
});

const LINE_ORDER = { fresh: 0, grill: 1, mart: 2 };

/**
 * Express first, then the earliest ready-by, then the oldest. The server already sends this order;
 * sorting again keeps a refetch from shuffling cards when two land together.
 * @param {HandoffOrder[]} orders
 */
export function sortHandoff(orders) {
  return orders.slice().sort((a, b) => {
    if (a.speed !== b.speed) return a.speed === 'express' ? -1 : 1;
    const ra = a.readyBy ?? '';
    const rb = b.readyBy ?? '';
    if (ra !== rb) {
      if (!ra) return 1;
      if (!rb) return -1;
      return ra < rb ? -1 : 1;
    }
    if (a.createdAt !== b.createdAt) return a.createdAt < b.createdAt ? -1 : 1;
    return a.code < b.code ? -1 : a.code > b.code ? 1 : 0;
  });
}

/** Fresh, Grill, Grocery: the same order on every card. @param {HandoffStation[]} stations */
export function sortStations(stations) {
  return stations.slice().sort((a, b) => (LINE_ORDER[a.line] ?? 9) - (LINE_ORDER[b.line] ?? 9));
}

/**
 * The station's progress line. The server's label wins ("Weighed 2/3", "On the grill", "Picked 1/4");
 * a missing label falls back to the counts.
 * @param {HandoffStation} s
 */
export function stationText(s) {
  if (s.label && s.label.trim()) return s.label.trim();
  if (s.line === 'fresh') return `Weighed ${s.done}/${s.total}`;
  if (s.line === 'mart') return `Picked ${s.done}/${s.total}`;
  return STATE_VIEW[s.state]?.label ?? 'Waiting';
}

/** "1 bag" / "3 bags". @param {number} n */
export function bagText(n) {
  const k = Math.max(0, Math.trunc(n || 0));
  return `${k} ${k === 1 ? 'bag' : 'bags'}`;
}

/** How many stations are done. @param {HandoffOrder} o */
export function doneCount(o) {
  return o.stations.filter((s) => s.state === 'done').length;
}

/**
 * The rider line on a card.
 * @param {HandoffOrder['rider']} rider
 * @returns {{ text: string; tone: 'success' | 'warning' | 'neutral'; here: boolean }}
 */
export function riderView(rider) {
  if (!rider) return { text: 'No rider yet', tone: 'neutral', here: false };
  if (rider.atPickup || rider.status === 'at_pickup') return { text: `${rider.name} · At the counter`, tone: 'success', here: true };
  if (rider.status === 'picked_up' || rider.status === 'arriving') return { text: `${rider.name} · Picked up`, tone: 'neutral', here: false };
  return { text: `${rider.name} · On the way`, tone: 'warning', here: false };
}

/** The card's headline state. @param {HandoffOrder} o */
export function cardTitle(o) {
  if (o.allReady) return o.rider?.atPickup ? 'All ready · hand it over' : 'All ready';
  const left = o.stations.length - doneCount(o);
  return `${left} ${left === 1 ? 'station' : 'stations'} to go`;
}

/** Board caption counts. @param {HandoffOrder[]} orders */
export function boardCounts(orders) {
  return {
    total: orders.length,
    ready: orders.filter((o) => o.allReady).length,
    express: orders.filter((o) => o.speed === 'express').length,
    atCounter: orders.filter((o) => !!o.rider?.atPickup).length,
  };
}

/**
 * Which cards just changed in a way the packer should notice: a rider reached the counter, or an
 * order became all ready. The first look only records what is there (no chime on open).
 * @param {{ ready: Set<string>; here: Set<string> } | null} seen
 * @param {HandoffOrder[]} orders
 */
export function detectArrivals(seen, orders) {
  const ready = new Set(orders.filter((o) => o.allReady).map((o) => o.orderId));
  const here = new Set(orders.filter((o) => o.rider?.atPickup).map((o) => o.orderId));
  if (!seen) return { arrived: [], nowReady: [], seen: { ready, here } };
  const arrived = orders.filter((o) => here.has(o.orderId) && !seen.here.has(o.orderId));
  const nowReady = orders.filter((o) => ready.has(o.orderId) && !seen.ready.has(o.orderId));
  return { arrived, nowReady, seen: { ready, here } };
}

/**
 * The live-region sentence for what just changed (null = nothing to say).
 * @param {HandoffOrder[]} arrived
 * @param {HandoffOrder[]} nowReady
 */
export function arrivalMessage(arrived, nowReady) {
  const first = arrived[0];
  if (arrived.length === 1 && first) return `${first.rider ? `Rider ${first.rider.name}` : 'The rider'} is at the counter for ${first.code}.`;
  if (arrived.length > 1) return `${arrived.length} riders are at the counter.`;
  const r = nowReady[0];
  if (nowReady.length === 1 && r) return `${r.code} is all ready.`;
  if (nowReady.length > 1) return `${nowReady.length} orders are all ready.`;
  return null;
}

// ── Stuck (Product gaps, Phase 7 open decision 1) ─────────────────────────────────────────────
// Orders older than the Ops setting handoff.staleAfterMinutes come back with stuck: true and their
// age. Nothing is dropped: they fold into a "Stuck · n" group under the live board. Old servers don't
// send the flag, so a missing one means live.

/** @typedef {HandoffOrder & Partial<import('@popcart/core/v1').HandoffStuckFlag>} HandoffItem */

/** Stuck only when the server says so. @param {HandoffItem} o */
export function isStuck(o) {
  return o.stuck === true;
}

/**
 * Live cards (sorted as the board always was) and stuck ones, oldest first.
 * @param {readonly HandoffItem[]} orders
 * @returns {{ live: HandoffItem[]; stuck: HandoffItem[] }}
 */
export function splitStuck(orders) {
  /** @type {HandoffItem[]} */
  const live = [];
  /** @type {HandoffItem[]} */
  const stuck = [];
  for (const o of orders) (isStuck(o) ? stuck : live).push(o);
  const age = (/** @type {HandoffItem} */ o) => (Number.isFinite(o.ageMinutes) ? Number(o.ageMinutes) : -1);
  return {
    live: sortHandoff(live),
    stuck: stuck.sort((a, b) => age(b) - age(a) || (a.createdAt < b.createdAt ? -1 : a.createdAt > b.createdAt ? 1 : 0)),
  };
}

/**
 * How long an order has waited: "45 min", "3 h", "3 h 20 min", "2 d 5 h". Unknown → null.
 * @param {number | null | undefined} minutes
 */
export function ageText(minutes) {
  if (minutes === null || minutes === undefined || !Number.isFinite(minutes)) return null;
  const m = Math.max(0, Math.floor(minutes));
  if (m < 60) return `${m} min`;
  if (m < 24 * 60) {
    const h = Math.floor(m / 60);
    const r = m % 60;
    return r ? `${h} h ${r} min` : `${h} h`;
  }
  const d = Math.floor(m / (24 * 60));
  const h = Math.floor((m % (24 * 60)) / 60);
  return h ? `${d} d ${h} h` : `${d} d`;
}

/** The folded group's heading: "Stuck · 3". @param {number} n */
export function stuckTitle(n) {
  return `Stuck · ${n}`;
}
